// App-shell wiring for the ops panels' entity links (spec FR5). The @repo/ui/charts EntitySpotlight
// is app-agnostic (A7): it takes injected `onActivate(bid)` / `onHover(bid | null)` callbacks and
// never imports the viewer's spotlight/scene APIs. This hook produces those callbacks from the
// twin-viewer's real primitives — fly-to via useFlyToBid → SpotlightStore.fireSpotlight (re-clicks
// re-fire, fire-count protocol), and scene highlight via EventContext.setCurrentHoveredValue
// (the same channel AssetItem rows use). Ported verbatim from POC2's EntitySpotlight.
import { useCallback } from 'react';
import { useEventContext, useModeContext, useTwinContext } from '@repo/front-end-state';
import { entityKeyFor, findEntityWithBID } from '@repo/front-end-utils';
import { useFlyToBid } from '../../common/hooks/useFlyToBid';
import { useResetTwinView } from '../../common/hooks/useResetTwinView';

export interface OpsEntityActions {
  onActivate: (bid: string) => void;
  onHover: (bid: string | null) => void;
  /** The "Show whole twin" dashboard component's request (ops-clear-selection FR1/FR2). */
  onShowWholeTwin: () => void;
}

export const useOpsEntityActions = (): OpsEntityActions => {
  const flyToBid = useFlyToBid();
  const onShowWholeTwin = useResetTwinView();
  const { setCurrentHoveredValue } = useEventContext();
  const { twin } = useTwinContext();
  const { appMode } = useModeContext();

  const onActivate = useCallback((bid: string) => flyToBid(bid), [flyToBid]);

  const onHover = useCallback(
    (bid: string | null) => {
      if (!bid) {
        setCurrentHoveredValue(null);
        return;
      }
      const entity = twin?.physicalModel ? findEntityWithBID(twin.physicalModel, bid) : undefined;
      const id = entity?.[entityKeyFor(appMode)];
      // Clear any prior highlight when the bid can't be resolved, rather than leaving the previous
      // entity stuck highlighted.
      setCurrentHoveredValue(typeof id === 'string' && id ? { id, type: 'ENTITY' } : null);
    },
    [twin, appMode, setCurrentHoveredValue],
  );

  return { onActivate, onHover, onShowWholeTwin };
};
